import { useCallback } from "react";
import {
  Table,
  Group,
  Stack,
  Text,
  Badge,
  Tooltip,
  ActionIcon,
  Collapse,
  Box,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconEdit,
  IconX,
  IconCheck,
} from "@tabler/icons-react";
import { Stain, StainType } from "../../../services/admin/stains.service";
import ProviderCapabilitiesPanel from "./ProviderCapabilitiesPanel";
import StainMarkerVerificationProviderBadge from "./StainMarkerVerificationProviderBadge";

const STAIN_TYPE_COLORS: Record<StainType, string> = {
  "H&E": "blue",
  IHC: "green",
  ISH: "violet",
  Special: "orange",
  Other: "gray",
};

interface StainAdminRowProps {
  stain: Stain;
  isExpanded: boolean;
  onToggleExpand: (id: string) => void;
  onEdit: (stain: Stain) => void;
  onToggleActive: (stain: Stain) => void;
}

export function StainAdminRow({
  stain,
  isExpanded,
  onToggleExpand,
  onEdit,
  onToggleActive,
}: StainAdminRowProps) {
  const handleToggleExpand = useCallback(() => {
    onToggleExpand(stain.id);
  }, [onToggleExpand, stain.id]);

  const handleEdit = useCallback(() => {
    onEdit(stain);
  }, [onEdit, stain]);

  const handleToggleActive = useCallback(() => {
    onToggleActive(stain);
  }, [onToggleActive, stain]);

  return (
    <>
      <Table.Tr
        style={{
          opacity: stain.isActive ? 1 : 0.5,
        }}
      >
        {/* Expand toggle */}
        <Table.Td>
          <ActionIcon variant="subtle" size="sm" onClick={handleToggleExpand}>
            {isExpanded ? (
              <IconChevronDown size={16} />
            ) : (
              <IconChevronRight size={16} />
            )}
          </ActionIcon>
        </Table.Td>

        {/* Stain (code + description) */}
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {stain.code}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {stain.description}
            </Text>
          </Stack>
        </Table.Td>

        {/* Markers (per-vendor; falls back to code) */}
        <Table.Td>
          <Stack gap={2}>
            <Group gap={8} wrap="nowrap" align="center">
              <Group gap={4} wrap="nowrap" style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">
                  Leica
                </Text>
                <Text size="xs">{stain.leicaMarker || stain.code}</Text>
              </Group>
              <StainMarkerVerificationProviderBadge
                summary={stain.markerVerification?.leica}
              />
            </Group>
            <Group gap={8} wrap="nowrap" align="center">
              <Group gap={4} wrap="nowrap" style={{ flex: 1 }}>
                <Text size="xs" c="dimmed">
                  Biocare
                </Text>
                <Text size="xs">{stain.biocareMarker || stain.code}</Text>
              </Group>
              <StainMarkerVerificationProviderBadge
                summary={stain.markerVerification?.biocare}
              />
            </Group>
          </Stack>
        </Table.Td>

        {/* Type */}
        <Table.Td>
          <Badge
            color={STAIN_TYPE_COLORS[stain.stainType]}
            variant="light"
            size="sm"
          >
            {stain.stainType}
          </Badge>
        </Table.Td>

        {/* Availability */}
        <Table.Td>
          {stain.isAvailableInHouse ? (
            <Badge color="green" variant="light" size="sm">
              In-House
            </Badge>
          ) : (
            <Tooltip
              label={stain.defaultProvider?.name || "No default provider"}
              withArrow
            >
              <Badge
                color="orange"
                variant="light"
                size="sm"
                style={{ cursor: "help" }}
              >
                External
              </Badge>
            </Tooltip>
          )}
        </Table.Td>

        {/* CPT Code */}
        <Table.Td>
          {stain.cptCode ? (
            <Tooltip label={stain.cptCode.description} withArrow>
              <Text size="sm" style={{ cursor: "help" }}>
                {stain.cptCode.code}
              </Text>
            </Tooltip>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>

        {/* Status */}
        <Table.Td>
          <Badge
            color={stain.isActive ? "green" : "red"}
            variant="light"
            size="sm"
          >
            {stain.isActive ? "Active" : "Inactive"}
          </Badge>
        </Table.Td>

        {/* Actions */}
        <Table.Td>
          <Group gap="xs">
            <Tooltip label="Edit">
              <ActionIcon variant="subtle" color="blue" onClick={handleEdit}>
                <IconEdit size={18} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={stain.isActive ? "Deactivate" : "Activate"}>
              <ActionIcon
                variant="subtle"
                color={stain.isActive ? "red" : "green"}
                onClick={handleToggleActive}
              >
                {stain.isActive ? <IconX size={18} /> : <IconCheck size={18} />}
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>

      {/* Expanded Row - Provider Capabilities */}
      <Table.Tr key={`${stain.id}-expanded`}>
        <Table.Td colSpan={8} p={0}>
          <Collapse in={isExpanded}>
            <Box p="md" bg="gray.0">
              <ProviderCapabilitiesPanel stain={stain} />
            </Box>
          </Collapse>
        </Table.Td>
      </Table.Tr>
    </>
  );
}
